'use client'
import { FixedBottomButton } from '@/app/ui/parts/default/sp/item/fixedButton'
import { ItemNumberSelector } from '@/app/ui/parts/default/sp/item/numberSelector'
import { ItemOptionSelectSlider } from '@/app/ui/parts/default/sp/item/optionslider'
import { Toaster } from '@/components/ui/sonner'
import { useAddCartItemOptions, useBuyItemOptions } from '@/lib/cart/logic'
import { getCartItems } from '@/lib/cart/server'
import { itemOptionType, itemType } from '@/lib/item/server'
import { serviceType } from '@/lib/service/server'
import { CheckCircle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { BuyButton } from '../parts/buttons/buyButton'
import { localItemOptionType } from './option'

export const useNumberSelectorECs = [
  'bookcoverly',
  'fairysailor',
  'pairheart',
  'perry-dog',
  'tiscase',
  'primeunders',
  'inu-happy',
  'mohumohudog',
  'modely',
  'prepsports',
  'girlishia',
  'swecolle',
  'syokunocanvas',
  'oshifuku',
  'bikeridge',
  'lovely-wear',
  'maid-mania',
  'slipperru',
  'hamster-cage-kingdom',
  'poppuppy',
  'cardy-cardy',
  'pumpti',
  'micobag',
  'notrea',
  'lolis',
  'casecraft',
  'cushionity',
  'gauze-collection',
  'littlecherry',
  'miraffi',
  'yosozee',
  'petitdress',
  'officasu',
  'stepchic',
  'jacketblue',
  'zoco-style',
  'nicopair',
  'carry-on-bag',
  'pinkyfashion',
  'corsagena',
  'longt-style',
  'gand1bestbuddy',
  'crossandhome',
  'cap-mania',
  'sweatlab',
  'stajans',
  'mellowdays',
  'sleepsmith',
  'jewelcoll',
  'wonderfulharness',
  'slipon-lab',
  'shirtia',
  'kirinsuit',
  'oshishika-katan',
  'denimmode',
  'stonavi',
  'simpletee',
  'redcloset',
  'bike-boots-mania',
  'blackessence',
  'carryfit',
  'lopina',
]

// オプションを選択して、購入もしくはカートに追加する
export const ItemOptionSelectForm_withOptions = ({
  service,
  item,
  itemOptions,
  promotionModal,
}: {
  service: serviceType
  item: itemType
  itemOptions: itemOptionType[]
  promotionModal: boolean
}) => {
  const localItemOptions = itemOptions.map((option) => ({
    ...option,
    size: option.size,
    color: option.color,
  })) as localItemOptionType[]
  const [selectedOption, setSelectedOption] = useState<localItemOptionType>(
    localItemOptions[0],
  )
  const [quantity, setQuantity] = useState(1)
  const [selecting, setSelecting] = useState(false)

  const disabled = selecting

  const [loadingBuy, submitBuy] = useBuyItemOptions({
    service,
    item,
  })
  const handleClickBuy = async () => {
    if (disabled) return
    await submitBuy({
      itemOptions: Array(quantity).fill(selectedOption),
    })
  }

  const [loadingAddCart, submitAddCart] = useAddCartItemOptions({
    service,
    item,
  })
  const handleClickAddCart = async () => {
    if (disabled) return
    await submitAddCart({
      itemOptions: Array(quantity).fill(selectedOption),
    })
    const cartItems = await getCartItems(service.service_id)
    toast(``, {
      description: (
        <div className="w-full my-4">
          <p className="font-semibold mb-4 text-gray-600">
            <CheckCircle className="inline-block mr-2" />
            カートに追加されました
          </p>
          <div className="flex items-center">
            <p className="ml-2 text-gray-600 font-semibold">
              {item.name} -{selectedOption.name}{' '}
              {selectedOption.size ? selectedOption.size.name : ''}
              {selectedOption.size && selectedOption.color ? ' / ' : ''}
              {selectedOption.color ? selectedOption.color.name : ''}
              {quantity > 1 ? ` x ${quantity}` : ''}
            </p>
          </div>
          <div className="mt-4 w-full">
            <button
              className="text-sm text-black font-semibold border border-black w-full p-2 mb-2"
              onClick={() => {
                window.location.href = '/transaction/address'
              }}
            >
              購入手続きへ
            </button>
            <button
              className="text-sm text-white font-semibold bg-black w-full p-2"
              onClick={() => {
                window.location.href = '/carts'
              }}
            >
              カートを見る({cartItems ? cartItems.length : 0})
            </button>
          </div>
        </div>
      ),
      position: 'top-center',
      duration: 4000,
    })
    setQuantity(1)
  }
  const upperText = useMemo(
    () =>
      `選択中の商品: ${selectedOption.name} ${selectedOption.size ? selectedOption.size.name : ''} ${selectedOption.size && selectedOption.color ? ' / ' : ''} ${selectedOption.color ? selectedOption.color.name : ''} ${quantity > 1 ? ` x ${quantity}` : ''}`,
    [selectedOption, quantity],
  )

  return (
    <div className="w-full mx-auto">
      <ItemOptionSelectSlider
        itemOptions={localItemOptions}
        selectedOption={selectedOption}
        onChanged={(option) => {
          setQuantity(1)
          setSelectedOption(option)
        }}
        onSelecting={(v) => {
          setSelecting(v)
        }}
      />
      {useNumberSelectorECs.includes(service.service_id) && (
        <div className="my-4">
          <ItemNumberSelector quantity={quantity} setQuantity={setQuantity} />
        </div>
      )}
      <div className="my-6">
        <div className="flex w-full mt-2">
          <BuyButton
            loading={loadingBuy}
            disabled={disabled}
            onClick={() => void handleClickBuy()}
            chevron={false}
            promotionModal={promotionModal}
          />
        </div>
        <div className="flex w-full mt-2">
          <BuyButton
            loading={loadingAddCart}
            disabled={disabled}
            onClick={() => void handleClickAddCart()}
            text="カートに追加する"
            chevron={false}
            addCart
            promotionModal={promotionModal}
          />
        </div>
      </div>
      {!promotionModal && <Toaster theme="light" />}
      {!promotionModal && (
        <FixedBottomButton
          onClick={() => void handleClickBuy()}
          loading={loadingBuy}
          disabled={disabled}
          text="購入画面に進む"
          upperText={upperText}
        />
      )}
    </div>
  )
}
